'use client';

import { useCallback, useEffect, useState } from 'react';

import { findFacilityExamsById } from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import { SearchApiFacilityExam } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { useDebounceValue } from '@/hooks/use-debounce.hook';

export const LABORATORY_EXAMS_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

export interface UseLaboratoryExamsOptions {
  enabled?: boolean;
}

export interface UseLaboratoryExamsReturn {
  exams: SearchApiFacilityExam[];
  term: string;
  setTerm(term: string): void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  hasError: boolean;
  hasLoadMoreError: boolean;
  loadMore(): void;
  retry(): void;
  retryLoadMore(): void;
}

export function useLaboratoryExams(
  facilityId: string,
  { enabled = true }: UseLaboratoryExamsOptions = {},
): UseLaboratoryExamsReturn {
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebounceValue(term, SEARCH_DEBOUNCE_MS);
  const effectiveTerm = term === '' ? '' : debouncedTerm;

  const [exams, setExams] = useState<SearchApiFacilityExam[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(enabled && Boolean(facilityId));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasLoadMoreError, setHasLoadMoreError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [previousTerm, setPreviousTerm] = useState(effectiveTerm);
  const [previousEnabled, setPreviousEnabled] = useState(enabled);
  const [previousFacilityId, setPreviousFacilityId] = useState(facilityId);

  if (
    effectiveTerm !== previousTerm
    || enabled !== previousEnabled
    || facilityId !== previousFacilityId
  ) {
    setPreviousTerm(effectiveTerm);
    setPreviousEnabled(enabled);
    setPreviousFacilityId(facilityId);
    setPage(1);
    setExams([]);
    setHasMore(false);
    setHasError(false);
    setHasLoadMoreError(false);
    setIsLoading(enabled && Boolean(facilityId));
    setIsLoadingMore(false);

    if (!enabled) {
      setTerm('');
    }
  }

  useEffect(() => {
    if (!enabled || !facilityId) {
      return;
    }

    let cancelled = false;
    const isFirstPage = page === 1;

    if (isFirstPage) {
      setIsLoading(true);
      setHasError(false);
    } else {
      setIsLoadingMore(true);
      setHasLoadMoreError(false);
    }

    findFacilityExamsById(facilityId, {
      page,
      limit: LABORATORY_EXAMS_PAGE_SIZE,
      term: effectiveTerm || undefined,
    })
      .then((result) => {
        if (cancelled) {
          return;
        }

        setExams((current) => (isFirstPage ? result.exams : [...current, ...result.exams]));
        setHasMore(result.hasMore);
        setHasError(false);
        setHasLoadMoreError(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        if (isFirstPage) {
          setHasError(true);
        } else {
          setHasLoadMoreError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, enabled, page, effectiveTerm, reloadToken]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) {
      return;
    }

    setPage((current) => current + 1);
  }, [hasMore, isLoading, isLoadingMore]);

  const retry = useCallback(() => {
    setPage(1);
    setReloadToken((current) => current + 1);
  }, []);

  const retryLoadMore = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  return {
    exams,
    term,
    setTerm,
    isLoading,
    isLoadingMore,
    hasMore,
    hasError,
    hasLoadMoreError,
    loadMore,
    retry,
    retryLoadMore,
  };
}
